'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { getConstruction, type Building } from '@repo/api';
import { PLACEHOLDER_REPORT_DATA } from '../../context/constants';
import { ConstructionSectionSkeleton } from './ConstructionSectionSkeleton';
import { ConstructionSection } from './ConstructionSection';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import { useAnalytics } from '@/hooks/useAnalytics';

interface ConstructionProps {
  building: Building;
  buildingAddress: string;
  showSkeleton: boolean;
  isReportReady: boolean;
}

function Construction({
  building,
  buildingAddress,
  showSkeleton,
  isReportReady,
}: ConstructionProps) {
  const analytics = useAnalytics();
  const sectionRef = useRef<HTMLDivElement>(null);
  const onScreen = useIntersectionObserver<HTMLDivElement>(
    sectionRef,
    useMemo(() => ({ threshold: 0.5 }), []),
  );

  const [baseConstructionData, setBaseConstructionData] = useState<any | null>(null);
  const [baseLoading, setBaseLoading] = useState(true);

  useEffect(() => {
    let timer: number | undefined;
    if (onScreen) {
      timer = window.setTimeout(() => {
        analytics.track('rentalreport_construction_scroll');
      }, 5000);
    }
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [analytics, onScreen]);

  useEffect(() => {
    if (!isReportReady) {
      setBaseLoading(false);
      setBaseConstructionData(PLACEHOLDER_REPORT_DATA.construction);
      return;
    }

    (async () => {
      setBaseLoading(true);

      try {
        const response = await getConstruction({ buildingId: building.id });
        setBaseConstructionData(response);
      } catch {
        setBaseConstructionData(null);
      } finally {
        setBaseLoading(false);
      }
    })();
  }, [isReportReady, building.id]);

  const shouldShowSkeleton = showSkeleton || baseLoading;

  if (shouldShowSkeleton) {
    return <ConstructionSectionSkeleton />;
  }

  return (
    <div ref={sectionRef}>
      <ConstructionSection
        buildingAddress={buildingAddress}
        isBlurRequired={!isReportReady}
        baseConstructionData={baseConstructionData}
      />
    </div>
  );
}

export default Construction;
